import React from "react";
import { notification as antdNotification } from "antd";
import {
  CheckCircleOutlined,
  InfoCircleOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { AxiosError } from "axios";

export interface NotificationParams {
  type: "success" | "info" | "warning" | "error";
  description: string;
  title?: string;
}

export const formatHTTPErrorMessage = (error: AxiosError) => {
  let { message } = error;
  const statusCode = error?.response?.status;
  const data = error?.response?.data;
  const cData = JSON.parse(JSON.stringify(data));
  // console.log("Notification Error Message: ", message, statusCode, data, cData);

  if (statusCode === 401) {
    if (cData.detail === "Given token not valid for any token type")
      return "Due to token expiration, for security purposes, you need to sign in again.";
    return cData.detail;
  }

  switch (statusCode) {
    case 402:
      return "Payment Required. Please check your payment details.";
    case 403:
      return "Forbidden. Please login again.";
    case 404:
      return "Not Found. The resource you are looking for does not exist.";
    case 405:
      return "Method Not Allowed. Please check the request method.";
    case 408:
      return "Request Timeout. Please try again later.";
    case 429:
      return "Too many requests! Try again later.";
    case 500:
      return "Internal Server Error. Please try again.";
    case 502:
      return "Bad Gateway. The server received an invalid response.";
    case 503:
      return "Service Unavailable. The server is currently unavailable.";
    case 504:
      return "Gateway Timeout. The server took too long to respond.";
    default:
      break;
  }

  // backend message process
  if (typeof data === "object" && data !== null) {
    const array: any[] = [];
    const keys = Object.keys(cData);
    keys.forEach((key) => {
      array.push(`${cData[key]}`);
    });
    message = array.join("\n");
    return message;
  }

  if (statusCode) {
    if (statusCode >= 400 && statusCode < 500)
      return "Client Error. Something went wrong with your request.";
    if (statusCode >= 500)
      return "Server Error. Something went wrong on our end.";
  }

  return message ?? "An unexpected error occurred. Please try again.";
};

export const notification = (params: NotificationParams) => {
  const { type, title, description } = params;
  const icons = {
    success: <CheckCircleOutlined style={{ color: "green" }} />,
    error: <CloseCircleOutlined style={{ color: "red" }} />,
    info: <InfoCircleOutlined style={{ color: "gray" }} />,
    warning: <ExclamationCircleOutlined style={{ color: "yellow" }} />,
  };
  const defaultTitle = {
    success: "Success",
    error: "Error",
    info: "Info",
    warning: "Warning",
  };
  antdNotification[type]({
    message: title || defaultTitle[type],
    description,
    placement: "bottomRight",
    icon: icons[type],
  });
};
